import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowUpRightIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const BLOG_URL = 'https://blog.csl-brands.com';
const BLOG_API = `${BLOG_URL}/wp-json/wp/v2`;

const getFeaturedImage = (post) =>
  post?._embedded?.['wp:featuredmedia']?.[0]?.media_details?.sizes?.large?.source_url
  || post?._embedded?.['wp:featuredmedia']?.[0]?.source_url
  || '/og-image.jpg';

const htmlToText = (value = '') => {
  if (typeof document === 'undefined') {
    return value.replace(/<[^>]*>/g, '');
  }

  const element = document.createElement('textarea');
  element.innerHTML = value.replace(/<[^>]*>/g, ' ');
  return element.value.replace(/\s+/g, ' ').trim();
};

const BlogSection = () => {
  const { t, i18n } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(0);
  const [status, setStatus] = useState('loading');
  const [requestId, setRequestId] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    fetch(`${BLOG_API}/categories?per_page=30&hide_empty=true&orderby=count&order=desc`, {
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error('Unable to load categories');
        return response.json();
      })
      .then((data) => {
        setCategories(data.filter((category) => category.slug !== 'uncategorized'));
      })
      .catch((error) => {
        if (error.name !== 'AbortError') setCategories([]);
      });

    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => {
      setStatus('error');
      controller.abort();
    }, 15000);
    const categoryQuery = activeCategory ? `&categories=${activeCategory}` : '';

    setStatus('loading');
    fetch(`${BLOG_API}/posts?per_page=5&orderby=date&order=desc&_embed=1${categoryQuery}`, {
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error('Unable to load posts');
        return response.json();
      })
      .then((data) => {
        setPosts(data);
        setStatus('success');
      })
      .catch((error) => {
        if (error.name !== 'AbortError') setStatus('error');
      })
      .finally(() => window.clearTimeout(timeout));

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [activeCategory, requestId]);

  const categoryNames = useMemo(
    () => new Map(categories.map((category) => [category.id, category.name])),
    [categories]
  );

  const retry = useCallback(() => setRequestId((value) => value + 1), []);

  const formatDate = (date) => new Intl.DateTimeFormat(
    i18n.language.startsWith('fr') ? 'fr-FR' : 'en-US',
    { day: 'numeric', month: 'short', year: 'numeric' }
  ).format(new Date(date));

  return (
    <section id="insights" className="bg-[#f4f1e9] py-16 sm:py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10 grid gap-8 border-b border-gray-900/15 pb-9 lg:grid-cols-[1fr_auto] lg:items-end">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.55 }}
          >
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.24em] text-csl-green">
              {t('blog.eyebrow')}
            </p>
            <h2 className="max-w-3xl text-3xl font-bold leading-tight text-gray-950 sm:text-4xl lg:text-6xl">
              {t('blog.title')}
            </h2>
          </motion.div>
          <a
            href={BLOG_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex w-fit items-center gap-2 border-b border-gray-900 pb-1 text-sm font-bold text-gray-900 transition-colors hover:border-csl-green hover:text-csl-green"
          >
            {t('blog.viewAll')}
            <ArrowUpRightIcon className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </a>
        </div>

        <div className="mb-10 flex gap-2 overflow-x-auto pb-2" aria-label={t('blog.categoriesLabel')}>
          <button
            type="button"
            onClick={() => setActiveCategory(0)}
            aria-pressed={activeCategory === 0}
            className={`shrink-0 rounded-full border px-5 py-2.5 text-sm font-semibold transition-colors ${activeCategory === 0 ? 'border-csl-green bg-csl-green text-white' : 'border-gray-900/20 bg-transparent text-gray-700 hover:border-csl-green hover:text-csl-green'}`}
          >
            {t('blog.allCategories')}
          </button>
          {categories.map((category) => (
            <button
              key={category.id}
              type="button"
              onClick={() => setActiveCategory(category.id)}
              aria-pressed={activeCategory === category.id}
              className={`shrink-0 rounded-full border px-5 py-2.5 text-sm font-semibold transition-colors ${activeCategory === category.id ? 'border-csl-green bg-csl-green text-white' : 'border-gray-900/20 bg-transparent text-gray-700 hover:border-csl-green hover:text-csl-green'}`}
            >
              {htmlToText(category.name)}
              <span className="ml-2 opacity-60">{category.count}</span>
            </button>
          ))}
        </div>

        {status === 'loading' && (
          <div className="grid gap-x-8 gap-y-10 md:grid-cols-2 lg:grid-cols-3" aria-label={t('blog.loading')}>
            {[0, 1, 2, 3, 4].map((item) => (
              <div key={item} className={item === 0 ? 'md:col-span-2 lg:row-span-2' : ''}>
                <div className={`skeleton mb-5 w-full ${item === 0 ? 'aspect-[16/9] lg:aspect-[4/3]' : 'aspect-[16/10]'}`} />
                <div className="skeleton mb-3 h-3 w-1/3" />
                <div className="skeleton h-7 w-4/5" />
              </div>
            ))}
          </div>
        )}

        {status === 'error' && (
          <div className="flex min-h-72 flex-col items-center justify-center border-y border-gray-900/15 text-center">
            <p className="mb-5 max-w-lg text-lg text-gray-600">{t('blog.error')}</p>
            <button
              type="button"
              onClick={retry}
              className="inline-flex items-center gap-2 rounded-full bg-csl-green px-6 py-3 font-semibold text-white transition-colors hover:bg-csl-dark-green"
            >
              <ArrowPathIcon className="h-5 w-5" />
              {t('blog.retry')}
            </button>
          </div>
        )}

        {status === 'success' && posts.length > 0 && (
          <motion.div
            key={activeCategory}
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
            className="grid gap-x-8 gap-y-12 md:grid-cols-2 lg:grid-cols-3"
          >
            {posts.map((post, index) => {
              const category = post.categories.map((id) => categoryNames.get(id)).find(Boolean);
              const isLead = index === 0;

              return (
                <motion.article
                  key={post.id}
                  variants={{ hidden: { opacity: 0, y: 18 }, visible: { opacity: 1, y: 0 } }}
                  transition={{ duration: 0.45 }}
                  className={isLead ? 'md:col-span-2 lg:row-span-2' : ''}
                >
                  <a href={post.link} target="_blank" rel="noopener noreferrer" className="group block">
                    <div className={`mb-5 overflow-hidden bg-gray-200 ${isLead ? 'aspect-[16/9] lg:aspect-[4/3]' : 'aspect-[16/10]'}`}>
                      <img
                        src={getFeaturedImage(post)}
                        alt={htmlToText(post.title.rendered)}
                        loading="lazy"
                        onError={(event) => {
                          if (!event.currentTarget.src.endsWith('/og-image.jpg')) event.currentTarget.src = '/og-image.jpg';
                        }}
                        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.035]"
                      />
                    </div>
                    <div className="mb-3 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.14em] text-csl-green">
                      <span>{category ? htmlToText(category) : t('blog.insight')}</span>
                      <span className="h-px w-5 bg-csl-green/50" />
                      <time dateTime={post.date} className="text-gray-500">{formatDate(post.date)}</time>
                    </div>
                    <h3 className={`${isLead ? 'text-2xl sm:text-3xl lg:text-4xl' : 'text-xl sm:text-2xl'} max-w-3xl font-bold leading-tight text-gray-950 transition-colors group-hover:text-csl-green`}>
                      {htmlToText(post.title.rendered)}
                    </h3>
                    {isLead && (
                      <p className="mt-4 max-w-2xl text-base leading-relaxed text-gray-600 sm:text-lg">
                        {htmlToText(post.excerpt.rendered).slice(0, 190)}
                      </p>
                    )}
                  </a>
                </motion.article>
              );
            })}
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default BlogSection;
